
import React, { useEffect, useState } from 'react';
import { subscriptionService } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';
import AllowanceWidget from './AllowanceWidget';
import MeetingLinkCta from '../../Shared/MeetingLinkCta';
import TierBilling from '../../Texas/TierBilling';

export default function SubscriptionTab() {
    // US-134/135: the Texas (USD) market renders the tier-billing UI in
    // account settings (T-134-002); the legacy subscription surface stays for
    // the UK market.
    const isTexas = getAppMarket() === 'texas';

    const [subscription, setSubscription] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isTexas) return;
        const fetchSub = async () => {
            try {
                const res = await subscriptionService.getMySubscriptions();
                const subs = Array.isArray(res.data) ? res.data : [res.data];
                const active = subs.find((s: any) => s.status === 'active') || subs[0];
                setSubscription(active);
            } catch (error) {
                console.error("Failed to fetch subscription", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSub();
    }, [isTexas]);

    if (isTexas) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Subscription & Billing</h3>
                <TierBilling />
            </div>
        );
    }

    if (loading) return <div className="p-4"><i className="fas fa-spinner fa-spin"></i> Loading...</div>;

    // Mock data if no sub found (Freemium logic)
    const subName = subscription?.productName || "Free Plan";
    const status = subscription?.status || "active";
    const propertyLimit = subscription?.plan_tier === 'pro' ? 50 : 1;
    const propertiesUsed = 1; // Ideally fetched from stats

    // Calculate percentage
    const percentage = Math.min((propertiesUsed / propertyLimit) * 100, 100);

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Subscription & Billing</h3>

            <AllowanceWidget />

            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h4 className="text-xl font-bold text-gray-900">{subName}</h4>
                        <p className="text-sm text-gray-500">
                            Status: <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{status.toUpperCase()}</span>
                        </p>
                    </div>
                    {/* Only show 'Manage' if it's a paid plan or we have a link */}
                    <button
                        onClick={() => window.open('https://compliancehubb.onfastspring.com/account', '_blank')}
                        className="text-sm text-green-600 hover:text-green-500 font-medium"
                    >
                        Manage Billing <i className="fas fa-external-link-alt ml-1"></i>
                    </button>
                </div>

                <div className="mb-2">
                    <div className="flex justify-between text-sm font-medium text-gray-900 mb-1">
                        <span>Property Usage</span>
                        <span>{propertiesUsed} / {propertyLimit === 50 ? '50+' : propertyLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className={`bg-green-600 h-2.5 rounded-full`}
                            style={{ width: `${percentage}%` }}
                        ></div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                        {propertyLimit === 1
                            ? "You are on the Free plan. Upgrade to manage more properties."
                            : "You have plenty of capacity for your portfolio."}
                    </p>
                </div>

                {propertyLimit === 1 && (
                    <div className="mt-6">
                        <button
                            onClick={() => window.location.href = '/product-selector'}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                        >
                            <i className="fas fa-crown mr-2"></i>
                            Upgrade to Pro
                        </button>
                    </div>
                )}

                {/* F-188 / T-165-002: meeting-link CTA (upgrade-flow surface) */}
                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-sm text-gray-500">Questions about your plan? Talk to a human.</p>
                    <MeetingLinkCta
                        tier={subscription?.plan_tier || 'free'}
                        campaign="subscription-tab"
                        label="Book a call"
                        className="text-sm text-green-600 hover:text-green-500 font-medium"
                    />
                </div>
            </div>
        </div>
    );
}
